/**
 * Embodied-carbon component type keys used by CalculationsSummary.
 */
export const getCodeDrawerEmbodiedComponentType = (componentType) => {
    const typeMap = {
        Wall: "wall",
        Lintel: "lintel",
        Window: "window",
        BasementWall: "basementWall",
        CrawlspaceWall: "crawlspaceWall",
        PonyWall: "ponyWall",
        Floor: "expFloor",
        FloorHeader: "floorHeader",
        Ceiling: "ceiling",
        CeilingFlat: "ceilingFlat",
        FloorsAbove: "floorsAbove",
        FloorsAdded: "floorsAdded",
    };

    return typeMap[componentType] || componentType[0]?.toLowerCase() + componentType.slice(1);
};

/**
 * Redux path for the enclosure component tied to an open code drawer.
 */
// export const getCodeDrawerComponentPath = (componentType, componentId) => {
//     if (!componentId) return null;

//     const componentKeyMap = {
//         Wall: "wall",
//         Lintel: "wall", // Note: could also be basement or crawlspace
//         Floor: "expFloor",
//         Ceiling: "ceiling",
//         CeilingFlat: "ceiling",
//         FloorHeader: "floorHeader",
//         BasementWall: "basement",
//         CrawlspaceWall: "crawlspace",
//         PonyWall: "basement",
//         FloorsAbove: "basement", // Note: could also be crawlspace
//         FloorsAdded: "basement", // Note: could alsoo be crawlspace or slab
//     };

//     const componentKey = componentKeyMap[componentType];
//     if (!componentKey) return null;

//     return `model.modelData.components.${componentKey}.${componentId}`;
// };

/**
 * Component path within modelData.components for the enclosure component tied to an open code drawer.
 */
export const getModelDataComponentPath = (componentType, fieldAccessor) => {
    const componentPath = fieldAccessor.replace("model.modelData.components.", "");

    switch (componentType) {
        case "PonyWall":        // removes .wall.ponyWall.ponyWallInsType
            return componentPath.split(".").slice(0, -3).join(".");
        case "FloorsAbove":     // removes .floor.floorsAboveInsType
        case "BasementWall":    // removes .wall.intAddedInsType
        case "CrawlspaceWall":  // removes .wall.intAddedInsType
        case "Lintel":          // removes .wall.lintelInsType
            return componentPath.split(".").slice(0, -2).join(".");
        case "Ceiling":         // removes .ceilingInsType
        case "CeilingFlat":     // removes .ceilingInsType
        case "Floor":           // removes .expFloorInsType
        case "Wall":            // removes .wallInsType
        case "FloorHeader":     // removes .floorHeaderInsType
        case "FloorsAdded":     // removes .slabInsType
            return componentPath.split(".").slice(0, -1).join(".");
        case "Window":
            return componentPath;
    }
};

/**
 * Shape component data into the measurements object expected by CalculationsSummary.
 */
export const mapCodeDrawerComponentToMeasurements = (componentType, componentData = {}) => {
    switch (componentType) {
        case "basementWall":
        case "crawlspaceWall":
            return {
                wall: componentData?.wall?.measurements || {},
                floor: componentData?.floor?.measurements || {},
            };
        case "ponyWall":
            return {
                ponyWallHeight: componentData?.wall?.ponyWall?.ponyWallHeight ?? 0,
                exposedSurfacePerimeter: componentData?.exposedSurfacePerimeter ?? 0,
            };
        case "floorsAbove":
        case "floorsAdded":
            return {
                floor: componentData?.floor?.measurements || {},
            };
        default:
            return componentData?.measurements || {};
    }
};

/**
 * Calculate the area of a component using the measurements object expected by CalculationsSummary.
 */
export const getComponentArea = (componentType = "", measurements = {}) => {
    if (componentType === "basementWall" || componentType === "crawlspaceWall") {
        const {
            wall: { height = 0 } = {},
            floor: { isRectangular = false, length = 0, width = 0, perimeter = 0 } = {},
        } = measurements || {};
        const floorPerimeter = isRectangular ? 2 * (length + width) : perimeter;

        return floorPerimeter * height;
    } else if (componentType === "floorsAbove" || componentType === "floorsAdded") {
        const { floor: { area = 0, length = 0, width = 0, isRectangular = false } = {} } = measurements || {};

        return isRectangular ? (length * width) : area;
    } else if (componentType === "ponyWall") {
        const { ponyWallHeight = 0, exposedSurfacePerimeter = 0 } = measurements || {};

        return exposedSurfacePerimeter * ponyWallHeight;
    }

    // ceiling, ceilingFlat, wall, expFloor, floorHeader, window
    return measurements?.area || 0;
};

/**
 * Get the general name of a component for the display.
 */
export const getCodeDrawerComponentName = (componentType) => {
    const nameMap = {
        ceiling: "ceiling",
        ceilingFlat: "ceiling",
        wall: "wall",
        expFloor: "exposed floor",
        basementWall: "foundation wall",
        crawlspaceWall: "crawlspace wall",
        floorsAbove: "floors above",
        floorHeader: "floor header",
        window: "window",
        ponyWall: "pony wall",
        lintel: "lintel",
        floorsAdded: "slab",
    };

    return nameMap[componentType] || componentType;
};